/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { UserManager, WebStorageStateStore, Log } from 'oidc-client';
import { getIdentityConfig } from './authConst';
import {
  setSessionCookie,
  clearSessionCookie,
  readSessionCookie,
  SESSION_POLL_INTERVAL_MS,
} from './sessionSync';

type JwtClaims = { sub?: string; iat?: number };

function decodeJwtClaims(jwt: string | null): JwtClaims | undefined {
  if (!jwt) return undefined;
  try {
    const base64Url = jwt.split('.')[1];
    if (!base64Url) return undefined;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      )
    );
    if (typeof payload?.exp === 'number' && payload.exp * 1000 <= Date.now()) {
      return undefined;
    }
    return {
      sub: payload?.sub ? String(payload.sub) : undefined,
      iat: typeof payload?.iat === 'number' ? payload.iat : undefined,
    };
  } catch {
    return undefined;
  }
}

async function getCurrentClaims(
  userManager: UserManager
): Promise<JwtClaims | undefined> {
  // App stores idToken in localStorage directly. Use it as the source of truth
  // for the user's sub; fall back to the UserManager.
  const fromIdToken = decodeJwtClaims(localStorage.getItem('idToken'));
  if (fromIdToken?.sub) return fromIdToken;

  const user: any = await userManager.getUser();
  if (!user?.profile?.sub) return undefined;
  return {
    sub: String(user.profile.sub),
    iat: typeof user.profile.iat === 'number' ? user.profile.iat : undefined,
  };
}

async function getCurrentSub(
  userManager: UserManager
): Promise<string | undefined> {
  return (await getCurrentClaims(userManager))?.sub;
}

// Tokens issued within this window are considered "fresh" — i.e., coming from
// a just-completed sign-in / silent renew. Older tokens are assumed to be
// rehydrated from localStorage and must not revive a cleared cuc-session.
const FRESH_TOKEN_WINDOW_SECONDS = 30;

function maybeReviveSessionCookie(sub: string, iat?: number): void {
  const existing = readSessionCookie();
  if (existing === sub) return;
  if (existing !== null) return;
  const tokenAge = iat ? Date.now() / 1000 - iat : Infinity;
  if (tokenAge <= FRESH_TOKEN_WINDOW_SECONDS) setSessionCookie(sub);
}

export default class AuthService {
  private userManager: UserManager;

  constructor() {
    this.userManager = new UserManager({
      ...getIdentityConfig(),
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    });
    // Logger
    Log.logger = console;
    Log.level = Log.DEBUG;
    this.userManager.events.addSilentRenewError((e) => {
      console.log('silent renew error', e.message);
    });

    this.userManager.events.addAccessTokenExpired(() => {
      console.log('token expired');
    });

    this.setupSessionSync();
  }

  /**
   * Cross-tab logout sync across *.cucollaborate.com:
   *   - on user load / app boot: derive sub from idToken (or UserManager) and
   *     write cuc-session=<sub>
   *   - on UserManager sign-out / unload: clear it
   *   - every 2s: re-derive sub; if cookie no longer matches, clear local
   *     auth state and reload — forcing re-auth if the IdP session is gone.
   *
   * First-party cookie at .cucollaborate.com isn't subject to third-party
   * storage partitioning, so sibling tabs at other *.cucollaborate.com
   * subdomains detect logout within ~2s.
   */
  private setupSessionSync(): void {
    this.userManager.events.addUserLoaded((user: any) => {
      const sub = user?.profile?.sub;
      if (!sub) return;
      const iat =
        typeof user.profile.iat === 'number' ? user.profile.iat : undefined;
      maybeReviveSessionCookie(String(sub), iat);
    });
    this.userManager.events.addUserSignedOut(clearSessionCookie);
    this.userManager.events.addUserUnloaded(clearSessionCookie);

    getCurrentClaims(this.userManager).then((claims) => {
      if (claims?.sub) maybeReviveSessionCookie(claims.sub, claims.iat);
    });

    window.setInterval(async () => {
      const sub = await getCurrentSub(this.userManager);
      if (!sub) return;
      if (readSessionCookie() !== sub) {
        await this.userManager.removeUser();
        // App stores its own auth state in these localStorage keys
        // independently of oidc-client's user store; clear them too.
        localStorage.removeItem('token');
        localStorage.removeItem('idToken');
        window.location.reload();
      }
    }, SESSION_POLL_INTERVAL_MS);
  }

  public getUser(): Promise<any> {
    return this.userManager.getUser();
  }

  public login({ redirectUrl }: { redirectUrl: string }): Promise<void> {
    localStorage.setItem('redirectUrl', redirectUrl);
    return this.userManager.signinRedirect();
  }

  public logout(): Promise<void> {
    // Clear shared cuc-session cookie before navigating to IdP /connect/logout
    // so sibling .cucollaborate.com SPAs detect this logout on their next poll.
    clearSessionCookie();
    return this.userManager.signoutRedirect({
      id_token_hint: localStorage.getItem('idToken'),
    });
  }

  public getAccessToken(): Promise<string> {
    return this.userManager.getUser().then((data: any) => data);
  }
}
